import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

function ReopenChartBody({
  points,
  onPointClick,
}: {
  points: Array<Record<string, number | string | null>>;
  onPointClick?: (bucketLabel: string) => void;
}) {
  const plotH = useDynamicsPlotHeight(280);
  return (
    <ResponsiveContainer width="100%" height={plotH}>
      <BarChart
        data={points}
        margin={{ ...dynamicsBucketChartMargins }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={(e) => {
          const label = (e as { activeLabel?: string } | null | undefined)?.activeLabel;
          if (onPointClick && typeof label === 'string') onPointClick(label);
        }}
      >
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis {...dynamicsPeriodAxisProps} />
        <YAxis />
        <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
        <Bar dataKey="reopens" name="Reopens" fill="#a855f7" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export const DynamicsH_ReopenDynamics: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="H"
      title="Reopen dynamics"
      hint="Count of reopen transitions (Done/Verified → In Progress) per period. One of the most honest quality indicators — reopens are never planned."
      needs="Status-transition sync — run Full Update once after configuring Quality settings."
      metric="reopens"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={280}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.H.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.H.bullets]}
    >
      {(points, onPointClick) => <ReopenChartBody points={points} onPointClick={onPointClick} />}
    </DynamicsChartFrame>
  );
};
